import { useCallback, useMemo, useState, type FC } from "react";
import { message, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFlowActions from "src/layouts/Engagements/Campaigns/shared/CampaignFlowActions";
import { CampaignStepCardChromeContext } from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import EmailStatusPopup from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/EmailStatusPopup";
import ChatScheduleStep from "./ChatScheduleStep";
import ChooseWhatsAppTemplateStep from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/steps/ChooseWhatsAppTemplateStep";
import WhatsAppEditorStep from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/steps/WhatsAppEditorStep";
import WhatsAppTemplateFormScreen from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/steps/WhatsAppTemplateFormScreen";
import type { WhatsAppTemplate } from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignTypes";
import {
  WHATSAPP_CAMPAIGN_STEPS,
  WHATSAPP_EDITOR_KEY,
} from "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignConstants";
import type { useChatWhatsAppCampaignWizard, ChatWhatsAppStepSnapshot } from "./useChatWhatsAppCampaignWizard";
import "src/layouts/Engagements/Campaigns/shared/CampaignFlow.scss";
import "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignWizard.scss";
import "src/layouts/Engagements/Campaigns/Tabs/WhatsAppCampaign/WhatsAppCampaignEditor.scss";
import "./ChatCampaignWizard.scss";

const WIZARD_KEY = "campaignFlow.aiWizard";
const EDITOR_STEP_INDEX = WHATSAPP_CAMPAIGN_STEPS.findIndex(step => step.key === "editor");

export type { ChatWhatsAppStepSnapshot as FrozenWhatsAppSnapshot } from "./useChatWhatsAppCampaignWizard";

interface ChatWhatsAppCampaignWizardProps {
  wizard: ReturnType<typeof useChatWhatsAppCampaignWizard>;
  frozenSnapshot?: ChatWhatsAppStepSnapshot;
}

const ChatWhatsAppCampaignWizard: FC<ChatWhatsAppCampaignWizardProps> = ({
  wizard,
  frozenSnapshot,
}) => {
  const { t, i18n } = useTranslation();
  const frozen = Boolean(frozenSnapshot);
  const {
    templatesState,
    wizard: whatsAppWizard,
    useAi,
    campaignName,
    sendTo,
    setCampaignName,
    setSendTo,
  } = wizard;

  const stepIndex = frozenSnapshot?.stepIndex ?? whatsAppWizard.stepIndex;
  const stepKey = frozenSnapshot?.stepKey ?? whatsAppWizard.stepKey;
  const template = frozenSnapshot?.template ?? whatsAppWizard.template;
  const drafts = frozenSnapshot?.drafts ?? whatsAppWizard.drafts;
  const media = frozenSnapshot?.media ?? whatsAppWizard.media;

  const {
    isComplete,
    isSending,
    isScheduleOpen,
    deliveryStatus,
    audience,
    preview,
    updateDraft,
    attachMedia,
    removeMedia,
    goNext,
    goBack,
    selectTemplate,
    changeTemplate,
    sendNow,
    schedule,
    openSchedule,
    closeSchedule,
    clearDeliveryStatus,
  } = whatsAppWizard;

  const [formTemplate, setFormTemplate] = useState<WhatsAppTemplate | null | undefined>();
  const isFormOpen = !frozen && formTemplate !== undefined;
  const isEditorStep = stepKey === "editor";

  const { reload: reloadTemplates } = templatesState;
  const openNewTemplateForm = useCallback(() => setFormTemplate(null), []);
  const closeTemplateForm = useCallback(() => setFormTemplate(undefined), []);
  const finishTemplateForm = useCallback(() => {
    setFormTemplate(undefined);
    reloadTemplates();
  }, [reloadTemplates]);
  const showDraftUnavailable = useCallback(
    () => message.info(t("campaignFlow.draft.whatsappUnavailable")),
    [t]
  );

  const displayStepIndex = isFormOpen ? EDITOR_STEP_INDEX : stepIndex;
  const stepCountLabel = useMemo(
    () =>
      t("wizardStepper.stepOf", {
        step: formatNumber(displayStepIndex + 1, {}, i18n.language),
        total: formatNumber(WHATSAPP_CAMPAIGN_STEPS.length, {}, i18n.language),
      }),
    [displayStepIndex, i18n.language, t]
  );

  const canSend = isComplete && campaignName.trim().length > 0 && sendTo.length > 0;
  const isNextDisabled = !template;

  const footer = frozen || !isScheduleOpen ? (
    <CampaignFlowActions
      onBack={goBack}
      wide
      note={isEditorStep ? undefined : t(`${WIZARD_KEY}.chatNote`)}
    >
      <button
        type="button"
        className="button button--link campaign-flow__action campaign-flow__action--link"
        onClick={showDraftUnavailable}
      >
        {t("campaignFlow.common.saveDraft")}
      </button>
      {isEditorStep ? (
        <>
          <button
            type="button"
            className="button button--primary campaign-flow__action"
            onClick={openSchedule}
            disabled={!canSend}
          >
            {t(`${WIZARD_KEY}.createCampaign.scheduleCampaign`)}
          </button>
          <button
            type="button"
            className="button button--primary campaign-flow__action"
            onClick={sendNow}
            disabled={!canSend}
          >
            {t(`${WIZARD_KEY}.createCampaign.sendCampaign`)}
          </button>
        </>
      ) : (
        <Tooltip
          title={isNextDisabled ? t(`${WHATSAPP_EDITOR_KEY}.chooseTemplate.nextDisabledHint`) : ""}
          placement="topRight"
          arrow={false}
          overlayClassName="wa-next-tooltip"
        >
          <span className="wa-next-tooltip__trigger">
            <button
              type="button"
              className="button button--primary campaign-flow__action"
              onClick={goNext}
              disabled={isNextDisabled}
            >
              {t(`${WIZARD_KEY}.next`)}
            </button>
          </span>
        </Tooltip>
      )}
    </CampaignFlowActions>
  ) : undefined;

  const chrome = { stepLabel: stepCountLabel, footer };
  const frozenClass = frozen ? " chat-campaign-wizard--frozen" : "";

  const renderStep = () => {
    if (isFormOpen) {
      return (
        <WhatsAppTemplateFormScreen
          template={formTemplate}
          sendTo={sendTo}
          useAi={useAi}
          onBack={closeTemplateForm}
          onFinished={finishTemplateForm}
          embedded
        />
      );
    }
    if (isEditorStep && template) {
      return (
        <WhatsAppEditorStep
          template={template}
          drafts={drafts}
          audience={audience}
          media={media}
          preview={preview}
          onDraftChange={updateDraft}
          onAttachMedia={attachMedia}
          onRemoveMedia={removeMedia}
          onChangeTemplate={changeTemplate}
          campaignName={campaignName}
          sendTo={sendTo}
          onCampaignNameChange={setCampaignName}
          onSendToChange={setSendTo}
        />
      );
    }
    return (
      <ChooseWhatsAppTemplateStep
        templatesState={templatesState}
        onUse={selectTemplate}
        onEdit={setFormTemplate}
        onCreate={openNewTemplateForm}
      />
    );
  };

  return (
    <div className={`campaign-flow chat-campaign-wizard${frozenClass}`}>
      <CampaignStepCardChromeContext.Provider value={chrome}>{renderStep()}</CampaignStepCardChromeContext.Provider>

      {!frozen && isScheduleOpen && (
        <ChatScheduleStep isSubmitting={isSending} onCancel={closeSchedule} onSchedule={schedule} />
      )}

      {!frozen && deliveryStatus && (
        <EmailStatusPopup
          type={deliveryStatus.type}
          date={deliveryStatus.date}
          time={deliveryStatus.time}
          onClose={clearDeliveryStatus}
        />
      )}
    </div>
  );
};

export default ChatWhatsAppCampaignWizard;
